/**
 * ============================================================================
 * ThresholdBandsDialog — Phase 6b frontend (Threshold Matrix Admin)
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: edit dialog for a single metric's green/amber/red boundaries. The
 *          field set is driven by the threshold polarity (LOWER_IS_BETTER vs
 *          HIGHER_IS_BETTER) so the user never sees a "green_min" field on a
 *          lower-is-better metric.
 *
 * REGULATORY:
 *   Quality thresholds are GMP_CRITICAL — saving requires TWO distinct
 *   signature bundle ids and a free-text change reason. Server enforces
 *   "distinct signer" and the audit binding.
 *
 * A11Y:
 *   - role="dialog" + aria-modal + aria-labelledby + aria-describedby.
 *   - Number inputs declare inputMode="decimal" so iOS shows the numeric
 *     keyboard; min/step come from the metric kind.
 *   - Validation errors surface in a <div role="alert"> with focus left on
 *     the offending field.
 * ============================================================================ */

import { useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@edms/design-system';
import dialogStyles from '../AuthorWorkbench/NewDocumentForm.module.css';
import styles from './ThresholdMatrixAdmin.module.css';
import { useUpdateThresholdMutation } from './useUpdateThresholdMutation';
import type {
  MetricThreshold,
  ThresholdBoundaries,
} from './useThresholdsQuery';

export interface ThresholdBandsDialogProps {
  threshold: MetricThreshold;
  onClose: () => void;
  onSaved: () => void;
}

function unitSuffix(kind: MetricThreshold['kind']): string {
  switch (kind) {
    case 'PERCENTAGE': return '%';
    case 'DAYS': return 'd';
    case 'COUNT': return '';
  }
}

function defaultStep(kind: MetricThreshold['kind']): string {
  return kind === 'PERCENTAGE' ? '0.1' : '1';
}

export function ThresholdBandsDialog({
  threshold,
  onClose,
  onSaved,
}: ThresholdBandsDialogProps) {
  const { t } = useTranslation();
  const titleId = useId();
  const descId = useId();
  const updateM = useUpdateThresholdMutation();

  // Local form state. We keep boundaries as strings so an empty input is
  // distinguishable from "0" and so we can validate before coercing.
  const [greenMax, setGreenMax] = useState<string>(
    threshold.boundaries.green_max != null ? String(threshold.boundaries.green_max) : '',
  );
  const [amberMax, setAmberMax] = useState<string>(
    threshold.boundaries.amber_max != null ? String(threshold.boundaries.amber_max) : '',
  );
  const [greenMin, setGreenMin] = useState<string>(
    threshold.boundaries.green_min != null ? String(threshold.boundaries.green_min) : '',
  );
  const [amberMin, setAmberMin] = useState<string>(
    threshold.boundaries.amber_min != null ? String(threshold.boundaries.amber_min) : '',
  );
  const [redMax, setRedMax] = useState<string>(
    threshold.boundaries.red_max != null ? String(threshold.boundaries.red_max) : '',
  );

  const [changeReason, setChangeReason] = useState('');
  const [sigA, setSigA] = useState('');
  const [sigB, setSigB] = useState('');
  const [error, setError] = useState<string | null>(null);

  const isLowerBetter = threshold.polarity === 'LOWER_IS_BETTER';
  const unit = useMemo(() => unitSuffix(threshold.kind), [threshold.kind]);
  const step = useMemo(() => defaultStep(threshold.kind), [threshold.kind]);

  const metricLabel = t(`qualityMetrics.metric.${threshold.metric_key}.label`, {
    defaultValue: threshold.label,
  });

  function parseNum(s: string): number | null {
    if (s.trim() === '') return null;
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    const boundaries: ThresholdBoundaries = {};

    if (isLowerBetter) {
      const g = parseNum(greenMax);
      const a = parseNum(amberMax);
      if (g == null || a == null) {
        setError(t('qualityMetrics.thresholds.errors.boundariesRequired', {
          defaultValue: 'Green-max and amber-max are required.',
        }));
        return;
      }
      if (g > a) {
        setError(t('qualityMetrics.thresholds.errors.greenLEAmber', {
          defaultValue: 'Green-max must be less than or equal to amber-max.',
        }));
        return;
      }
      boundaries.green_max = g;
      boundaries.amber_max = a;
      const r = parseNum(redMax);
      if (r != null) {
        if (r < a) {
          setError(t('qualityMetrics.thresholds.errors.redGEAmber', {
            defaultValue: 'Red-max must be greater than or equal to amber-max.',
          }));
          return;
        }
        boundaries.red_max = r;
      }
    } else {
      const g = parseNum(greenMin);
      const a = parseNum(amberMin);
      if (g == null || a == null) {
        setError(t('qualityMetrics.thresholds.errors.boundariesRequired', {
          defaultValue: 'Green-min and amber-min are required.',
        }));
        return;
      }
      if (g < a) {
        setError(t('qualityMetrics.thresholds.errors.greenGEAmber', {
          defaultValue: 'Green-min must be greater than or equal to amber-min.',
        }));
        return;
      }
      boundaries.green_min = g;
      boundaries.amber_min = a;
    }

    if (changeReason.trim().length < 10) {
      setError(t('qualityMetrics.thresholds.errors.reasonTooShort', {
        defaultValue: 'Change reason must be at least 10 characters.',
      }));
      return;
    }
    if (!sigA.trim() || !sigB.trim()) {
      setError(t('qualityMetrics.thresholds.errors.dualSignRequired', {
        defaultValue: 'Two signature bundle ids are required.',
      }));
      return;
    }
    if (sigA.trim() === sigB.trim()) {
      setError(t('qualityMetrics.thresholds.errors.dualSignDistinct', {
        defaultValue: 'The two signers must be different.',
      }));
      return;
    }

    try {
      await updateM.mutateAsync({
        metricKey: threshold.metric_key,
        boundaries,
        changeReason: changeReason.trim(),
        signatureBundleIdA: sigA.trim(),
        signatureBundleIdB: sigB.trim(),
      });
      onSaved();
    } catch (e: unknown) {
      setError(
        e instanceof Error
          ? e.message
          : t('qualityMetrics.thresholds.errors.serverFailed', {
              defaultValue: 'Server rejected the change.',
            }),
      );
    }
  }

  return (
    <div
      className={dialogStyles.backdrop}
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={dialogStyles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
      >
        <header className={dialogStyles.header}>
          <h2 id={titleId} className={dialogStyles.title}>
            {t('qualityMetrics.thresholds.dialog.title', {
              defaultValue: 'Edit thresholds: {{metric}}',
              metric: metricLabel,
            })}
          </h2>
          <p id={descId} className={dialogStyles.desc}>
            {t('qualityMetrics.thresholds.dialog.subtitle', {
              defaultValue:
                'GMP-Critical change. Activation requires two distinct signature bundles and a change reason.',
            })}
          </p>
        </header>

        <form className={dialogStyles.form} onSubmit={onSubmit} noValidate>
          {error && (
            <div role="alert" className={dialogStyles.errorBanner}>
              {error}
            </div>
          )}

          <div className={styles.polarityHint} role="note">
            <strong>
              {t(`qualityMetrics.thresholds.polarity.${threshold.polarity}.title`, {
                defaultValue: isLowerBetter ? 'Lower is better' : 'Higher is better',
              })}
            </strong>{' '}
            ·{' '}
            {t(`qualityMetrics.thresholds.polarity.${threshold.polarity}.body`, {
              defaultValue: isLowerBetter
                ? 'Values at or below green-max are GREEN; at or below amber-max are AMBER; above are RED.'
                : 'Values at or above green-min are GREEN; at or above amber-min are AMBER; below are RED.',
            })}
          </div>

          {isLowerBetter ? (
            <div className={styles.boundaryGrid}>
              <BoundaryInput
                id="th-green-max"
                label={t('qualityMetrics.thresholds.fields.greenMax', {
                  defaultValue: 'Green max',
                })}
                value={greenMax}
                onChange={setGreenMax}
                step={step}
                suffix={unit}
                bandClass={styles.boundaryGreen}
              />
              <BoundaryInput
                id="th-amber-max"
                label={t('qualityMetrics.thresholds.fields.amberMax', {
                  defaultValue: 'Amber max',
                })}
                value={amberMax}
                onChange={setAmberMax}
                step={step}
                suffix={unit}
                bandClass={styles.boundaryAmber}
              />
              <BoundaryInput
                id="th-red-max"
                label={t('qualityMetrics.thresholds.fields.redMax', {
                  defaultValue: 'Red max (optional)',
                })}
                value={redMax}
                onChange={setRedMax}
                step={step}
                suffix={unit}
                bandClass={styles.boundaryRed}
              />
            </div>
          ) : (
            <div className={styles.boundaryGrid}>
              <BoundaryInput
                id="th-green-min"
                label={t('qualityMetrics.thresholds.fields.greenMin', {
                  defaultValue: 'Green min',
                })}
                value={greenMin}
                onChange={setGreenMin}
                step={step}
                suffix={unit}
                bandClass={styles.boundaryGreen}
              />
              <BoundaryInput
                id="th-amber-min"
                label={t('qualityMetrics.thresholds.fields.amberMin', {
                  defaultValue: 'Amber min',
                })}
                value={amberMin}
                onChange={setAmberMin}
                step={step}
                suffix={unit}
                bandClass={styles.boundaryAmber}
              />
            </div>
          )}

          <div className={dialogStyles.field}>
            <label htmlFor="th-reason" className={dialogStyles.label}>
              {t('qualityMetrics.thresholds.fields.reason', {
                defaultValue: 'Change reason',
              })}
              <span className={dialogStyles.required} aria-hidden="true">*</span>
            </label>
            <textarea
              id="th-reason"
              className={dialogStyles.input}
              rows={3}
              value={changeReason}
              onChange={(e) => setChangeReason(e.target.value)}
              placeholder={t('qualityMetrics.thresholds.fields.reasonPlaceholder', {
                defaultValue:
                  'e.g., Aligning amber threshold with regional QMS revision 4.2',
              })}
              required
            />
          </div>

          <div className={styles.dualSigGrid}>
            <div className={dialogStyles.field}>
              <label htmlFor="th-sig-a" className={dialogStyles.label}>
                {t('qualityMetrics.thresholds.fields.sigA', {
                  defaultValue: 'Signature bundle id — SIGNER A',
                })}
                <span className={dialogStyles.required} aria-hidden="true">*</span>
              </label>
              <input
                id="th-sig-a"
                className={dialogStyles.input}
                value={sigA}
                onChange={(e) => setSigA(e.target.value)}
                placeholder="sig-…"
                required
              />
            </div>
            <div className={dialogStyles.field}>
              <label htmlFor="th-sig-b" className={dialogStyles.label}>
                {t('qualityMetrics.thresholds.fields.sigB', {
                  defaultValue: 'Signature bundle id — SIGNER B',
                })}
                <span className={dialogStyles.required} aria-hidden="true">*</span>
              </label>
              <input
                id="th-sig-b"
                className={dialogStyles.input}
                value={sigB}
                onChange={(e) => setSigB(e.target.value)}
                placeholder="sig-…"
                required
              />
            </div>
          </div>

          <footer className={dialogStyles.footer}>
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={updateM.isPending}
            >
              {t('common.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button type="submit" variant="primary" isLoading={updateM.isPending}>
              {t('qualityMetrics.thresholds.actions.save', {
                defaultValue: 'Save thresholds',
              })}
            </Button>
          </footer>
        </form>
      </div>
    </div>
  );
}

interface BoundaryInputProps {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  step: string;
  suffix: string;
  bandClass: string;
}

function BoundaryInput({
  id,
  label,
  value,
  onChange,
  step,
  suffix,
  bandClass,
}: BoundaryInputProps) {
  return (
    <div className={`${styles.boundaryField} ${bandClass}`}>
      <label htmlFor={id} className={styles.boundaryLabel}>
        {label}
      </label>
      <div className={styles.boundaryInputWrap}>
        <input
          id={id}
          type="number"
          inputMode="decimal"
          step={step}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={styles.boundaryInput}
        />
        {suffix && <span className={styles.boundarySuffix}>{suffix}</span>}
      </div>
    </div>
  );
}
